import { type CSSProperties, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import { useSelector } from 'react-redux';
import { useIsomorphicLayoutEffect } from 'react-use';

import { ViewTypeName } from '@air/api/types';
import { Portal } from '@air/primitive-portal';

import { GalleryItemsDragPreview } from '~/components/ReactDndDragLayer/GalleryItemsDragPreview';
import { LibraryDragPreview } from '~/components/ReactDndDragLayer/LibraryDragPreview';
import { NavBoardDragPreview } from '~/components/ReactDndDragLayer/NavBoardDragPreview';
import { TableItemsDragPreview } from '~/components/ReactDndDragLayer/TableItemsDragPreview';
import { useDragScrolling } from '~/components/ReactDndDragLayer/useDragScrolling';
import { useNativeDragTracking } from '~/components/ReactDndDragLayer/useNativeDragTracking';
import { WorkspaceDragPreview } from '~/components/ReactDndDragLayer/WorkspaceDragPreview';
import { getDragGrabAnchor } from '~/components/Shared/Drag/dragGrabAnchor';
import { DragContainerIds, DragType } from '~/components/Shared/Drag/dragTypes';
import { CANVAS_ASSET_DRAG_PREVIEW_MAX_SIZE, DRAG_GHOST_CURSOR_OFFSET } from '~/constants/dnd';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';

const layerStyles: CSSProperties = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 100,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
};

export const ReactDndDragLayer = memo(() => {
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const dragItem = useRef<HTMLDivElement | null>(null);
  const requestRef = useRef<number | undefined>(undefined);
  const isDraggingRef = useRef<boolean>();
  const offsetRef = useRef<{ x: number; y: number } | undefined>(undefined);

  const { itemType, isDragging, item, clientOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    clientOffset: monitor.getClientOffset(),
  }));

  const grabOffsetRef = useRef({ x: 0, y: 0 });

  useIsomorphicLayoutEffect(() => {
    if (!isDragging || dragItem.current === null) {
      grabOffsetRef.current = { x: 0, y: 0 };
      return;
    }

    const { x: anchorX, y: anchorY } = getDragGrabAnchor();

    // Prefer the thumbnail element so the offset accounts for its position within
    // the preview container (padding, stacked card chrome, etc.). dragItem is at the
    // viewport origin before the RAF moves it, so getBoundingClientRect gives accurate
    // relative positions between elements even before the first paint.
    const thumbnailEl = dragItem.current.querySelector<HTMLElement>('img, video');

    if (thumbnailEl) {
      const containerRect = dragItem.current.getBoundingClientRect();
      const thumbRect = thumbnailEl.getBoundingClientRect();
      grabOffsetRef.current = {
        x: thumbRect.left - containerRect.left + thumbRect.width * anchorX,
        y: thumbRect.top - containerRect.top + thumbRect.height * anchorY,
      };
      return;
    }

    // Fallback for previews without an img/video (boards, etc.): use the first
    // descendant with non-zero dimensions — absolute children collapse the wrapper.
    for (const child of dragItem.current.querySelectorAll<HTMLElement>('*')) {
      const { width, height } = child.getBoundingClientRect();

      if (width > 0 && height > 0) {
        grabOffsetRef.current = { x: width * anchorX, y: height * anchorY };
        break;
      }
    }
  }, [isDragging]); // useIsomorphicLayoutEffect: fires synchronously after commit so grabOffsetRef is set before the RAF runs

  const { nativeMousePositionRef, clientOffsetRef } = useNativeDragTracking({
    isDragging,
    clientOffset,
    dragItemRef: dragItem,
    offsetRef,
    grabOffsetRef,
  });

  /**
   * TODO: We shouldn't be assigning a specific drag type to a specific scroll container.
   * Ideally a user can drag any type over any scrollable container in our application and
   * it will cause that container to scroll
   */
  const dragContainer = useMemo(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    switch (itemType) {
      case DragType.boardNavFavorites:
      case DragType.boardNavLibrary:
      case DragType.library:
        return document.getElementById(DragContainerIds.navBoard);
      case DragType.workspace:
        return document.getElementById(DragContainerIds.workspace);
      case DragType.version:
        return document.getElementById(DragContainerIds.versions);
      default:
        return document.getElementById(DragContainerIds.other);
    }
  }, [itemType]);

  useDragScrolling({ scrollElement: dragContainer ?? undefined });

  const moveItem = useCallback(() => {
    // Prefer native dragover position (fires continuously) over React DnD position
    const currentOffset = nativeMousePositionRef.current ?? clientOffsetRef.current ?? offsetRef.current;

    if (currentOffset !== undefined) {
      offsetRef.current = currentOffset;
    }

    if (dragItem.current !== null && offsetRef.current !== undefined) {
      const { x, y } = offsetRef.current;
      const { x: gx, y: gy } = grabOffsetRef.current;
      const transform = `translate(${x - gx - DRAG_GHOST_CURSOR_OFFSET}px, ${y - gy - DRAG_GHOST_CURSOR_OFFSET}px)`;
      dragItem.current.style.transform = transform;
      dragItem.current.style.webkitTransform = transform;
      if (dragItem.current.style.visibility !== 'visible') {
        dragItem.current.style.visibility = 'visible';
      }
    }
    requestRef.current = requestAnimationFrame(moveItem);
  }, [clientOffsetRef, nativeMousePositionRef]);

  useEffect(() => {
    if (isDraggingRef.current !== isDragging && isDragging) {
      requestRef.current = requestAnimationFrame(moveItem);
    } else if (isDraggingRef.current !== isDragging && !isDragging) {
      if (dragItem.current !== null) {
        dragItem.current.style.visibility = 'hidden';
        dragItem.current.style.transform = 'none';
        dragItem.current.style.webkitTransform = 'none';
      }
      if (requestRef.current !== undefined) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = undefined;
      }
    }
    isDraggingRef.current = isDragging;
  }, [isDragging, moveItem]);

  useEffect(() => {
    return () => {
      if (requestRef.current !== undefined) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, []);

  const itemToRender = useMemo(() => {
    switch (itemType) {
      case DragType.boardNavWorkspace:
      case DragType.boardNavFavorites:
      case DragType.boardNavLibrary:
        return <NavBoardDragPreview board={item} />;
      case DragType.boardCard:
      case DragType.asset:
      case DragType.file:
        return currentViewTypeName === ViewTypeName.gallery ? (
          <GalleryItemsDragPreview items={item} />
        ) : (
          <TableItemsDragPreview items={item} />
        );
      case DragType.assetPicker:
        return <GalleryItemsDragPreview items={item} thumbnailMaxSize={CANVAS_ASSET_DRAG_PREVIEW_MAX_SIZE} />;
      case DragType.workspace:
        return <WorkspaceDragPreview item={item} />;
      case DragType.library:
        return <LibraryDragPreview library={item} />;
      default:
        return null;
    }
  }, [itemType, item, currentViewTypeName]);

  return (
    <Portal>
      <div style={layerStyles}>
        <div ref={dragItem} style={{ visibility: 'hidden' }}>
          {isDragging ? itemToRender : null}
        </div>
      </div>
    </Portal>
  );
});

ReactDndDragLayer.displayName = 'ReactDndDragLayer';
